import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { askMeiAi } from "@/utils/ai.functions";
import { saveChatMessage, listChatMessages } from "@/lib/localHistory";
import { Bot, Send, Loader2, History } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/ferramentas/ia-mei")({
  head: () => ({
    meta: [
      { title: "IA tira-dúvidas do MEI — MEI Toolbox" },
      {
        name: "description",
        content:
          "Assistente de IA gratuito para o MEI: tire dúvidas sobre DAS, DASN, limite de faturamento, ocupações permitidas, nota fiscal e Simples Nacional em segundos.",
      },
      { property: "og:title", content: "IA tira-dúvidas do MEI — MEI Toolbox" },
      {
        property: "og:description",
        content:
          "Assistente de IA gratuito para o MEI: tire dúvidas sobre DAS, DASN, limite de faturamento, ocupações permitidas, nota fiscal e Simples Nacional em segundos.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/ferramentas/ia-mei" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/ferramentas/ia-mei" }],
  }),
  component: AiPage,
});

function AiPage() {
  const [messages, setMessages] = useState<{ role: "user" | "assistant"; content: string }[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const conversationIdRef = useRef<string>(crypto.randomUUID());
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Carrega a última conversa salva neste navegador
  useEffect(() => {
    const all = listChatMessages();
    if (all.length > 0) {
      const last = all[all.length - 1].conversation_id;
      conversationIdRef.current = last;
      setMessages(
        all
          .filter((m) => m.conversation_id === last)
          .map((m) => ({ role: m.role, content: m.content })),
      );
    }
    setHistoryLoaded(true);
  }, []);

  const persist = (role: "user" | "assistant", content: string) => {
    saveChatMessage({
      conversation_id: conversationIdRef.current,
      role,
      content,
      created_at: new Date().toISOString(),
    });
  };

  const startNewConversation = () => {
    conversationIdRef.current = crypto.randomUUID();
    setMessages([]);
  };

  const send = async () => {
    if (!input.trim() || sending) return;
    const userMsg = { role: "user" as const, content: input };
    const next = [...messages, userMsg];
    setMessages(next);
    setInput("");
    setSending(true);
    persist("user", userMsg.content);
    try {
      const res = await askMeiAi({ data: { messages: next } });
      setMessages([...next, { role: "assistant", content: res.content }]);
      persist("assistant", res.content);
    } catch (e: any) {
      toast.error(e.message || "Erro ao responder");
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="container mx-auto max-w-3xl px-4 py-8 flex flex-col min-h-[calc(100vh-12rem)]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-amber-600">
          <Bot className="h-5 w-5" /> <span className="text-xs font-bold uppercase">IA</span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/historico"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground"
          >
            <History className="h-3.5 w-3.5" /> Histórico
          </Link>
          <button
            onClick={startNewConversation}
            className="text-xs font-semibold text-amber-600 hover:underline"
          >
            Nova conversa
          </button>
        </div>
      </div>
      <h1 className="mt-2 text-2xl font-bold">IA tira-dúvidas do MEI</h1>

      <div className="mt-6 flex-1 overflow-y-auto rounded-2xl border border-border bg-card p-4 space-y-4">
        {historyLoaded && messages.length === 0 && (
          <div className="text-center text-sm text-muted-foreground py-8">
            <p>Pergunte qualquer coisa sobre MEI:</p>
            <ul className="mt-3 space-y-1 text-xs">
              <li>"Quando vence o DAS de fevereiro?"</li>
              <li>"Posso ter funcionário sendo MEI?"</li>
              <li>"Como faço para dar baixa no CNPJ?"</li>
            </ul>
            <p className="mt-4 text-[11px]">
              Suas conversas ficam salvas neste navegador e disponíveis no{" "}
              <Link to="/historico" className="text-amber-600 underline">
                histórico
              </Link>
              .
            </p>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-amber-500 text-white" : "bg-muted"}`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl bg-muted px-4 py-2">
              <Loader2 className="h-4 w-4 animate-spin" />
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Digite sua pergunta…"
          className="flex-1 rounded-full border border-border bg-background px-4 py-2.5 text-sm"
        />
        <button
          onClick={send}
          disabled={sending}
          aria-label="Enviar pergunta"
          className="rounded-full bg-amber-500 hover:bg-amber-600 px-5 text-white disabled:opacity-60"
        >
          <Send className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <article className="mt-12 prose prose-sm max-w-none dark:prose-invert">
        <h2>Assistente de IA gratuito para o Microempreendedor Individual</h2>
        <p>
          A IA tira-dúvidas do MEI Toolbox foi treinada para responder perguntas práticas do dia a
          dia do Microempreendedor Individual: DAS-MEI, DASN-SIMEI, limite de faturamento, ocupações
          permitidas, emissão de nota fiscal, INSS, aposentadoria, contratação de funcionário,
          mudança para ME e desenquadramento. Em vez de procurar em fóruns ou esperar atendimento no
          Portal do Empreendedor, você descreve sua situação em português natural e recebe uma
          orientação objetiva em segundos.
        </p>

        <h3>Para que serve este assistente</h3>
        <p>
          Use a IA quando precisar entender uma regra rápida (datas de vencimento, valor atualizado
          do DAS, quem pode ser MEI), comparar cenários (continuar como MEI ou migrar para ME, sair
          do CLT para abrir CNPJ), interpretar boletos e guias, simular consequências de atraso, ou
          simplesmente confirmar se uma informação que você ouviu é verdadeira. Para cálculos
          exatos, combine a resposta da IA com nossas{" "}
          <Link to="/ferramentas">calculadoras especializadas</Link>.
        </p>

        <h3>Perguntas comuns que a IA responde</h3>
        <ul>
          <li>Quando vence o DAS-MEI e o que acontece se eu atrasar?</li>
          <li>Estourei o limite de faturamento — sou desenquadrado automaticamente?</li>
          <li>Posso ser MEI e ter carteira assinada ao mesmo tempo?</li>
          <li>Como emitir nota fiscal eletrônica para pessoa jurídica sendo MEI?</li>
          <li>Quantos anos preciso contribuir para a aposentadoria por idade no MEI?</li>
          <li>Como fazer a Declaração Anual do MEI (DASN-SIMEI) passo a passo?</li>
          <li>Quais CNAEs estão permitidos para o MEI em 2026?</li>
        </ul>

        <h3>Privacidade e histórico</h3>
        <p>
          Cada conversa fica salva apenas neste navegador — nada é vinculado a um cadastro ou conta.
          Você pode revisar, retomar ou apagar qualquer conversa no{" "}
          <Link to="/historico">seu histórico local</Link>. Use o botão "Nova conversa" para começar
          do zero sempre que mudar de assunto; isso ajuda a IA a focar no contexto novo.
        </p>

        <h3>Limitações importantes</h3>
        <p>
          A IA é uma assistente de orientação geral, não substitui contador, advogado ou o
          atendimento oficial da Receita Federal. Para casos com risco fiscal alto (parcelamento de
          dívidas, baixa de CNPJ, contratação de sócio, mudança de natureza jurídica), consulte um
          contador. As informações são baseadas na legislação vigente do Simples Nacional para 2026,
          mas regras podem mudar — em caso de dúvida, sempre confirme no Portal do Empreendedor
          (gov.br/empresas-e-negocios).
        </p>
      </article>
    </section>
  );
}
